import { motion } from "framer-motion";
import { ArrowUpRight, BookOpen, Headphones, Compass, ShoppingBag } from "lucide-react";
import { BOOKS, waLink } from "../data/config";
import { Reveal } from "../components/common/Reveal";
import { BlogTeaser } from "../components/blog/BlogTeaser";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const MODES = [
  { icon: BookOpen, label: "Read", note: "Books, poetry, essays, reflections" },
  { icon: Headphones, label: "Listen", note: "Music and spoken word — coming soon" },
  { icon: Compass, label: "Discover", note: "Reviews, recommendations, other authors" },
  { icon: ShoppingBag, label: "Shop", note: "Ready to connect — checkout activates soon" },
];

export default function CreativePage() {
  return (
    <main data-testid="creative-page">
      <section className="bg-warmwhite py-32 lg:py-40" data-testid="creative-hero">
        <div className="container-site">
          <motion.p
            className="text-xs font-semibold uppercase tracking-[0.3em] text-amber"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            iINSPIRE Creative — Published by iINSPIRE Publishing
          </motion.p>
          <h1 className="mt-8 font-serif text-5xl leading-[1.02] tracking-tight text-ink sm:text-7xl" data-testid="creative-headline">
            <MaskedLine delay={0.35}>Stories. Words.</MaskedLine>
            <MaskedLine delay={0.55} className="italic text-amber">Music. Ideas.</MaskedLine>
          </h1>
          <motion.p
            className="mt-10 max-w-lg text-base leading-relaxed text-ink/70 sm:text-lg"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
          >
            The creative heart of the ecosystem — books, reflections and ideas
            from Irma, Luna, Mulan and invited voices. Every work carries its
            own author.
          </motion.p>
        </div>
      </section>

      <section className="border-y border-ink/10 bg-white py-14" data-testid="creative-modes">
        <div className="container-site grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {MODES.map((m, i) => (
            <Reveal key={m.label} delay={i * 0.08}>
              <div data-testid={`creative-mode-${m.label.toLowerCase()}`}>
                <m.icon className="h-5 w-5 text-amber" strokeWidth={1.25} />
                <p className="mt-4 text-xs font-semibold uppercase tracking-[0.25em] text-ink">{m.label}</p>
                <p className="mt-2 text-sm leading-relaxed text-ink/60">{m.note}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-warmwhite py-24 lg:py-32" data-testid="creative-books">
        <div className="container-site">
          <Reveal>
            <p className="eyebrow">Read</p>
            <h2 className="mt-4 font-serif text-3xl tracking-tight text-ink sm:text-4xl lg:text-5xl">
              Words for the <span className="italic text-amber">journey.</span>
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-10 sm:grid-cols-2">
            {BOOKS.map((book, i) => (
              <Reveal key={book.id} delay={i * 0.1}>
                <article className="grid gap-6 sm:grid-cols-5" data-testid={`creative-book-${book.id}`}>
                  <div className="relative sm:col-span-2">
                    <div className="absolute -left-3 -top-3 h-full w-full border border-amber/50" aria-hidden="true" />
                    {book.coverImage ? (
                      <img src={book.coverImage} alt={`${book.title} book cover`} loading="lazy" className="relative aspect-[3/4] w-full object-cover" />
                    ) : (
                      <div className="relative flex aspect-[3/4] flex-col justify-between bg-ink p-6">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber">{book.author}</p>
                        <p className="font-serif text-2xl text-warmwhite">{book.title}</p>
                      </div>
                    )}
                  </div>
                  <div className="sm:col-span-3">
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-ink/50">{book.language}</span>
                      <span className="border border-amber px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-amber">
                        {book.status}
                      </span>
                    </div>
                    <h3 className="mt-3 font-serif text-2xl text-ink">{book.title}</h3>
                    {book.subtitle && <p className="mt-1 font-serif text-sm italic text-ink/60">{book.subtitle}</p>}
                    <p className="mt-2 text-[11px] uppercase tracking-[0.2em] text-ink/45">by {book.author}</p>
                    <p className="mt-4 text-sm leading-relaxed text-ink/70">{book.synopsis}</p>
                    {book.excerpt && (
                      <blockquote className="mt-4 border-l-2 border-amber pl-4 font-serif text-base italic text-ink">
                        {book.excerpt}
                      </blockquote>
                    )}
                    <p className="mt-6 text-xs uppercase tracking-[0.2em] text-ink/50" data-testid={`creative-buy-${book.id}`}>
                      Purchase links coming soon
                    </p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <BlogTeaser
        eyebrow="Coffee With Dr. Irma · Stories"
        title="Stories worth"
        titleAccent="keeping."
        vertical="publishing"
        channel="coffee-stories"
        basePath="/creative/stories"
        linkLabel="All stories"
        testId="creative-stories-teaser"
        bg="bg-white"
      />

      <section className="bg-ink py-20 text-warmwhite lg:py-28" data-testid="creative-enquiry">
        <div className="container-site flex flex-col items-start gap-8 md:flex-row md:items-center md:justify-between">
          <Reveal>
            <p className="eyebrow">Enquiries</p>
            <h2 className="mt-4 font-serif text-3xl tracking-tight sm:text-4xl">
              Rights, orders & <span className="italic text-amber">collaborations.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <a
              href={waLink("Hi, I'm enquiring about iINSPIRE Creative — books / orders / collaborations.\nSource: Creative Website")}
              target="_blank"
              rel="noreferrer"
              className="btn-gold"
              data-testid="creative-whatsapp-cta"
            >
              Enquire on WhatsApp <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
            </a>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
